/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * The canvas inspector's trigger settings: which record type, which event,
 * and a description. The name and the on/off switch live in the status strip
 * above the canvas, so they are not repeated here. Same wizard state and
 * validation messages as the classic TriggerStep.
 */

import { useMemo } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import { v2 as t } from "styles/tokens/v2";
import { useAutomationContext } from "components/AutomationWizard/context/AutomationContext";
import { useValidation } from "components/AutomationWizard/hooks/useValidation";
import LabeledField from "./LabeledField";

const eventName = (id) => id.charAt(0).toUpperCase() + id.slice(1).replace("_", " ");

export default function TriggerEditor({ objects, extraTriggers }) {
  const { state, actions } = useAutomationContext();
  const { getFieldError } = useValidation();
  const object = state.wizardData.object || "";

  const sortedObjects = useMemo(
    () => Object.values(objects || {}).sort((a, b) => (a.type !== b.type ? (a.type === "standard" ? -1 : 1) : a.name.localeCompare(b.name))),
    [objects]
  );
  const events = useMemo(() => {
    const meta = objects?.[object];
    if (!meta) return [];
    return (meta.capabilities?.triggers || []).map((id) => ({ id, name: eventName(id) })).concat(extraTriggers || []);
  }, [objects, object, extraTriggers]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <LabeledField
        select
        required
        label="Record type"
        value={object}
        onChange={(e) => actions.updateWizardData({ object: e.target.value })}
        error={Boolean(getFieldError("object"))}
        helperText={getFieldError("object") || undefined}
        SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 320 } } } }}
      >
        {sortedObjects.map((o) => (
          <MenuItem key={o.id} value={o.id}>
            {o.name}
            {o.type === "custom" && <Box component="span" sx={{ ml: 1, fontSize: 11.5, color: t.text3 }}>Custom</Box>}
          </MenuItem>
        ))}
      </LabeledField>
      <LabeledField
        select
        required
        label="Starts when"
        value={events.some((e) => e.id === state.wizardData.trigger) ? state.wizardData.trigger : ""}
        disabled={!object}
        onChange={(e) => actions.updateWizardData({ trigger: e.target.value })}
        error={Boolean(getFieldError("trigger"))}
        helperText={getFieldError("trigger") || (object ? undefined : "Choose the record type first.")}
      >
        {events.map((ev) => (
          <MenuItem key={ev.id} value={ev.id}>{ev.name}</MenuItem>
        ))}
      </LabeledField>
      <LabeledField
        multiline
        minRows={2}
        label="Description"
        placeholder="What this automation is for"
        value={state.wizardData.description || ""}
        onChange={(e) => actions.updateWizardData({ description: e.target.value })}
      />
    </Box>
  );
}

TriggerEditor.propTypes = {
  objects: PropTypes.object,
  extraTriggers: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })),
};
TriggerEditor.defaultProps = { objects: null, extraTriggers: undefined };
